import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.autolistify.com';

const KeywordMetricsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [storeId, setStoreId] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('clicks');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;

  useEffect(() => {
    const init = async () => {
      try {
        const storeRes = await axios.get(`${API_URL}/api/stores`, { withCredentials: true });
        if (!storeRes.data?.length) {
          setError('No stores connected. Please connect a Shopify store first.');
          setLoading(false);
          return;
        }
        const sid = storeRes.data[0].id;
        setStoreId(sid);
        await fetchKeywords(sid);
      } catch (err) {
        setError('Failed to load store data.');
        setLoading(false);
      }
    };
    init();
  }, []);

  const fetchKeywords = async (sid) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/search-console/performance?storeId=${sid}`, {
        withCredentials: true,
      });
      // response is an array of page objects, each with a queries array
      // Flatten to per-keyword rows
      const keywordMap = {};
      (res.data || []).forEach((pageData) => {
        (pageData.queries || []).forEach((kw) => {
          if (!keywordMap[kw]) {
            keywordMap[kw] = { keyword: kw, clicks: 0, impressions: 0, ctrSum: 0, positionSum: 0, count: 0, pages: [] };
          }
          keywordMap[kw].clicks += pageData.clicks || 0;
          keywordMap[kw].impressions += pageData.impressions || 0;
          keywordMap[kw].ctrSum += parseFloat(pageData.avgCtr || 0);
          keywordMap[kw].positionSum += parseFloat(pageData.avgPosition || 0);
          keywordMap[kw].count += 1;
          if (!keywordMap[kw].pages.includes(pageData.page)) {
            keywordMap[kw].pages.push(pageData.page);
          }
        });
      });

      const flat = Object.values(keywordMap).map((kw) => ({
        keyword: kw.keyword,
        clicks: kw.clicks,
        impressions: kw.impressions,
        avgCtr: kw.count > 0 ? (kw.ctrSum / kw.count) : 0,
        avgPosition: kw.count > 0 ? (kw.positionSum / kw.count) : 0,
        pages: kw.pages.length,
      }));

      setRows(flat);
    } catch (err) {
      if (err.response?.status === 400) {
        setError('Search Console is not connected for this store. Go to Store Connection to connect it.');
      } else if (err.response?.status === 404) {
        setError('Google integration not found. Please connect Google first.');
      } else {
        setError(err.response?.data?.error || 'Failed to load keyword data.');
      }
    } finally {
      setLoading(false);
    }
  };

  const sorted = useMemo(() => {
    return [...rows]
      .filter((r) => !search || r.keyword.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        const av = a[sortKey] ?? 0;
        const bv = b[sortKey] ?? 0;
        return sortDir === 'desc' ? bv - av : av - bv;
      });
  }, [rows, search, sortKey, sortDir]);

  const totalPages = Math.ceil(sorted.length / PER_PAGE);
  const paged = sorted.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-indigo-600 ml-1">{sortDir === 'desc' ? '↓' : '↑'}</span>;
  };

  const ctrColor = (ctr) => {
    if (ctr >= 0.05) return 'text-green-600';
    if (ctr >= 0.02) return 'text-yellow-600';
    return 'text-red-500';
  };

  const posColor = (pos) => {
    if (pos <= 3) return 'text-green-600';
    if (pos <= 10) return 'text-yellow-600';
    return 'text-red-500';
  };

  const totalClicks = rows.reduce((s, r) => s + r.clicks, 0);
  const totalImpressions = rows.reduce((s, r) => s + r.impressions, 0);
  const avgCtrAll = rows.length ? rows.reduce((s, r) => s + r.avgCtr, 0) / rows.length : 0;
  const avgPosAll = rows.length ? rows.reduce((s, r) => s + r.avgPosition, 0) / rows.length : 0;

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Keyword Metrics</h2>
          <p className="text-gray-600 mt-1">Real keyword performance from Google Search Console</p>
        </div>
        {storeId && (
          <button
            onClick={() => fetchKeywords(storeId)}
            className="text-sm text-indigo-600 hover:text-indigo-800 border border-indigo-300 rounded px-3 py-1"
          >
            Refresh
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
          {error}
          {error.includes('not connected') && (
            <span className="block mt-1 text-sm">
              Go to <strong>Store Connection</strong> → Web Analytics → Connect GSC to enable keyword data.
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-500">Loading keyword data from Search Console…</span>
        </div>
      ) : !error && rows.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 text-lg">No keyword data found for the last 30 days.</p>
          <p className="text-gray-400 text-sm mt-2">Make sure Search Console is connected and your site has traffic.</p>
        </div>
      ) : !error && (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
              <p className="text-sm text-gray-500">Total Keywords</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">{rows.length.toLocaleString()}</h3>
              <p className="text-xs text-gray-400 mt-1">from Search Console</p>
            </div>
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
              <p className="text-sm text-gray-500">Total Clicks</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">{totalClicks.toLocaleString()}</h3>
              <p className="text-xs text-gray-400 mt-1">last 30 days</p>
            </div>
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
              <p className="text-sm text-gray-500">Total Impressions</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">{totalImpressions.toLocaleString()}</h3>
              <p className="text-xs text-gray-400 mt-1">last 30 days</p>
            </div>
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
              <p className="text-sm text-gray-500">Avg Position</p>
              <h3 className={`text-2xl font-bold mt-1 ${posColor(avgPosAll)}`}>{avgPosAll.toFixed(1)}</h3>
              <p className="text-xs text-gray-400 mt-1">Avg CTR: {(avgCtrAll * 100).toFixed(2)}%</p>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap gap-3 items-center justify-between">
              <h3 className="text-base font-semibold text-gray-800">
                Keyword Performance ({sorted.length} keywords)
              </h3>
              <input
                type="text"
                placeholder="Search keywords…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keyword</th>
                    {[
                      { key: 'clicks', label: 'Clicks' },
                      { key: 'impressions', label: 'Impressions' },
                      { key: 'avgCtr', label: 'CTR' },
                      { key: 'avgPosition', label: 'Avg Position' },
                      { key: 'pages', label: 'Pages' },
                    ].map(({ key, label }) => (
                      <th
                        key={key}
                        onClick={() => handleSort(key)}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-indigo-600 select-none"
                      >
                        {label}<SortIcon col={key} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paged.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-6 py-3 font-medium text-gray-900 max-w-xs truncate">{row.keyword}</td>
                      <td className="px-6 py-3 text-gray-700">{row.clicks.toLocaleString()}</td>
                      <td className="px-6 py-3 text-gray-700">{row.impressions.toLocaleString()}</td>
                      <td className={`px-6 py-3 font-medium ${ctrColor(row.avgCtr)}`}>
                        {(row.avgCtr * 100).toFixed(2)}%
                      </td>
                      <td className={`px-6 py-3 font-medium ${posColor(row.avgPosition)}`}>
                        {row.avgPosition.toFixed(1)}
                      </td>
                      <td className="px-6 py-3 text-gray-500">{row.pages}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, sorted.length)} of {sorted.length}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-100"
                  >
                    Previous
                  </button>
                  <button
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-100"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default KeywordMetricsPage;
